import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { envConfig } from '@/lib/env-config'

export type Theme = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function getSystemPreference(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function resolve(theme: Theme): 'light' | 'dark' {
  return theme === 'system' ? getSystemPreference() : theme
}

function applyTheme(resolved: 'light' | 'dark') {
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
  const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (favicon) {
    favicon.href =
      resolved === 'dark'
        ? '/favicon__black.ico'
        : `/${envConfig.faviconPrefix}favicon.ico`
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('super-theme')
    return stored === 'dark' || stored === 'light' || stored === 'system'
      ? stored
      : 'light'
  })

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    resolve(theme),
  )

  useEffect(() => {
    const resolved = resolve(theme)
    setResolvedTheme(resolved)
    applyTheme(resolved)
    localStorage.setItem('super-theme', theme)

    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        const next = e.matches ? 'dark' : 'light'
        setResolvedTheme(next)
        applyTheme(next)
      }
      mq.addEventListener('change', handler)
      return () => mq.removeEventListener('change', handler)
    }
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState(t => (resolve(t) === 'dark' ? 'light' : 'dark'))
  }, [])

  useEffect(() => {
    let lastCtrlMTime = 0
    const DOUBLE_PRESS_MS = 500

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'm') {
        e.preventDefault()
        const now = Date.now()
        if (now - lastCtrlMTime <= DOUBLE_PRESS_MS) {
          setThemeState(t => (resolve(t) === 'dark' ? 'light' : 'dark'))
          lastCtrlMTime = 0
        } else {
          lastCtrlMTime = now
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, setTheme, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
